/**
 * Provides baseline functionality for a Publisher to keep an internal value, immedidately return it upon request, and notify subscribers on change.
 * @class Publisher
 * @augments Class
 */
define("Publisher", [`Class`, `module`], (Super, { id: namespace }) =>
	Super.inherit({
		namespace,
		prepare(setup) {
			const { data } = this;
			const publishedValue = null; // used to compare when deciding to publish updates
			const value = {};

			this.assign(data, { publishedValue, value });
			this.defineProperty(`subscriptions`, { value: [] }); // make the assignment constant

			// NOTE: inheriting classes should have an initial value ready by the time constructor() is complete
		},
		api: {
			/**
			 * Immediately return the internal value, and potentially the requesting instance to the list of subscribers.
			 * @method Publisher#get
			 * @param {Object} options
			 * @param {string} options.dataProperty - Keep the requesting instance's data.____ value synced on publish
			 * @param {boolean} options.fireImmediately=false - Whether to call options.handler immediately
			 * @param {boolean} options.reconcile=false - Whether to call the requesting instances reconcile() function on publish
			 * @param {boolean} options.reconcileImmediately=false - Whether to trigger the requesting instance's reconcile() immediately
			 * @param {function} options.subscribe - A handler call when future updates are published
			 * @returns {Object} value
			 */
			get: {
				public: true,
				clone: true,
				defaults: {
					dataProperty: null,
					fireImmediately: false,
					reconcile: false,
					reconcileImmediately: false,
				},
				value(context, options) {
					const { data, subscriptions } = this;
					const { value } = data;
					const instance = context; // used to keep track of the requesting instance
					const { dataProperty, subscribe: handler } = options;
					const hasDataProperty = this.isString(dataProperty, 1);
					const hasHandler = this.isFunction(handler);

					if (hasHandler || hasDataProperty) {
						const { fireImmediately, reconcile, dataProperty } = options;

						if (reconcile && !this.isFunction(instance.reconcile)) {
							const { namespace } = instance;
							throw new Error(`Publisher.subscribe(options.reconcile: true) requires ${namespace}.reconcile()`);
						}

						subscriptions.push({ dataProperty, handler, instance, reconcile });

						if (fireImmediately) {
							const { reconcileImmediately } = options;

							if (hasDataProperty) {
								instance.data[dataProperty] = this.clone(value);
							}

							if (hasHandler) {
								handler.call(instance, this.clone(value));
							}

							if (reconcileImmediately) {
								instance.reconcile();
							}
						}
					}

					return value;
				},
			},

			/**
			 * A private function that checks the internal value for changes, publishing to the subscribers if any are found.
			 * @method Publisher#publish
			 */
			publish: {
				// private function for notifying all subscriptions that an update occurred
				value() {
					const { data, subscriptions } = this;
					const { publishedValue, value } = data;
					const updated = !this.isEqual(value, publishedValue);

					if (updated) {
						this.extract(subscriptions, (subscription) => {
							const { dataProperty, handler, instance, reconcile } = subscription;

							if (!instance.alive) {
								// skip, remove from subscribers
								return true;
							}

							this.defer({
								done: true,
								then: () => {
									if (this.isString(dataProperty, 1)) {
										instance.data[dataProperty] = this.clone(value);
									}

									if (this.isFunction(handler)) {
										handler.call(instance, this.clone(value));
									}

									if (reconcile) {
										// trigger the requesting instance's reconcile()
										instance.reconcile();
									}
								},
							});
						});
					}
				},
			},

			/**
			 * Apply changes to the internal value and call this.publish()
			 * @method Publisher#update
			 * @param {Object} options
			 * @param {Object} options.value - Properties to patch the internal value with
			 */
			update: {
				value(options) {
					const { data } = this;
					const { value } = data;

					this.assign(value, options);
					this.publish();
				},
			},

			/**
			 * Remove subscriptions to this publisher based on the passed criteria.
			 * @method Publisher#unsubscribe
			 * @param {Object} options
			 */
			unsubscribe: {
				public: true,
				defaults: {},
				value(context, options) {
					const instance = context;

					this.defaults(options, { instance });
					this.extract(this.subscriptions, options);
				},
			},
		},
	}));

//# sourceMappingURL=Publisher.js.map